import classNames from 'classnames';
import React, { useContext, useState } from 'react';
import {
  StorageProviderKey,
  StorageProviderKeyToReadableName,
} from '../../models/storageProviderKeys';
import { GoogleApiContext } from '../../services/gapi/gapiService';
import { LibraryContext } from '../../services/library/libraryService';
import { soundCloudEnabled } from '../../services/soundCloud/soundCloudEnabled';
import { mixcloudEnabled } from '../../services/mixcloud/mixcloudEnabled';
import { localStorageProviderEnabled } from '../../services/storage/localStorageProvider';
import { SoundCloudContext } from '../../services/soundCloud/soundCloudService';
import { spotifyEnabled } from '../../services/spotify/spotifyEnabled';
import { SpotifyContext } from '../../services/spotify/spotifyService';
import { Context } from '../../services/_globalContext/context';
import { Keys, messages } from '../../settings';
import ErrorBoundary from '../common/errorBoundary/errorBoundary';
import AuthorizeButton from '../common/authorizeButton/authorizeButton';
import BrandChip, { storageProviderBrands } from '../common/brandChip/brandChip';
import ImportItunes from '../common/importItunes/importItunes';
import InfoBubble from '../common/infoBubble/infoBubble';
import { showShortcuts } from '../common/shortcutsOverlay/shortcutsOverlay';
import {
  downloadAppDataInfo,
  itunesImportInfo,
  localStorageInfo,
  mixcloudInfo,
  podcastPathInfo,
  resetAppDataInfo,
  revokePermissionsInfo,
  saveLibraryInfo,
  spotifyInfo,
  storageProviderInfo,
  streamingInfo,
} from '../common/infoBubble/infoTexts';
import ProviderList from './providerList';
import ServiceRow from './serviceRow';
import ToggleSetting from './toggleSetting';
import s from './settingsComponent.module.scss';

interface Props {}

/**
 * Settings. Two groups of accounts - the one that holds the library, and
 * the ones music is played from - then the library controls, then Advanced.
 *
 * Every service is its logo, not its name, and its buttons just say Login
 * and Logout. Everything that used to be a paragraph under a control is an
 * (i) beside it (infoTexts.tsx); the one sentence left in the open is the
 * storage rule at the top, because it is the one that changes what happens
 * when you press something.
 */
export default function SettingsComponent({}: Props) {
  const ctx = useContext(Context);
  const libCtx = useContext(LibraryContext);
  const spotifyCtx = useContext(SpotifyContext);
  const soundCloudCtx = useContext(SoundCloudContext);
  const gapiCtx = useContext(GoogleApiContext);

  /**
   * The iTunes import dialog, which is a whole flow rather than a setting:
   * pick a file, pick playlists, read the report, then write. It is mounted
   * only while open, so a 70 MB parse holds nothing once it is closed.
   */
  const [importingItunes, setImportingItunes] = useState(false);

  const currentProvider = ctx.storageProvider.current?.name;

  function handleMixcloudUsernameSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    const form = ev.currentTarget;
    const input = form.elements.namedItem('mixcloud-username') as HTMLInputElement;

    ctx.setMixcloudUsername(input.value);
    ctx.callSnackbar(messages(Keys.saved), false);
  }

  function handleLibraryPathSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const inputEl = form.elements.namedItem('library-path') as HTMLInputElement;

    if (inputEl.value.startsWith('/')) {
      ctx.setLibraryPath(inputEl.value);
      ctx.callSnackbar(messages(Keys.saved), false);
    } else {
      ctx.callSnackbar(messages(Keys.ensurePathStringConformity));
    }
  }

  return (
    <ErrorBoundary>
      <article className={classNames('mainViewport', ctx.playerSize, s.settings)}>
        <section className={s.group}>
          <h3 className={s.groupHeading}>
            Storage:
            {/*
              The provider in use, as its own mark - the same chip its row
              below carries, so the two are recognisably the same thing. The
              development browser provider has no logo and keeps its name.
            */}
            {currentProvider && storageProviderBrands[currentProvider] ? (
              <BrandChip
                brand={storageProviderBrands[currentProvider]!}
                className={s.groupHeadingBrand}
              />
            ) : (
              <span className={s.groupHeadingValue}>
                {currentProvider ? StorageProviderKeyToReadableName[currentProvider] : 'none yet'}
              </span>
            )}
          </h3>
          {/* A div, not a p: the bubble's text is paragraphs, and a p cannot hold one. */}
          <div className={s.groupHelp}>
            Where your library and settings live. One at a time - switching starts the app again
            with the other one.
            <InfoBubble label={storageProviderInfo.label}>{storageProviderInfo.text}</InfoBubble>
          </div>
          <ProviderList />
        </section>

        <hr className={s.rule} />

        <section className={s.group}>
          <h3 className={s.groupHeading}>
            Streaming
            <InfoBubble label={streamingInfo.label}>{streamingInfo.text}</InfoBubble>
          </h3>
          <ul className={s.serviceList}>
            {soundCloudEnabled && (
              <ServiceRow
                brand="soundCloud"
                status={
                  soundCloudCtx.accessToken &&
                  (soundCloudCtx.userProfile?.full_name || soundCloudCtx.userProfile?.username)
                }
              >
                {soundCloudCtx.accessToken ? (
                  <button className="button" onClick={soundCloudCtx.logout}>
                    Logout
                  </button>
                ) : (
                  <AuthorizeButton
                    busy={soundCloudCtx.isAuthorizing}
                    onClick={() =>
                      soundCloudCtx.authorize({ allowRedirect: true }).catch(ctx.callSnackbar)
                    }
                  >
                    Login
                  </AuthorizeButton>
                )}
              </ServiceRow>
            )}

            {mixcloudEnabled && (
              /*
                A username, not a login, and not for want of trying: Mixcloud's
                OAuth authorize page answers 404 (docs/MIXCLOUD.md 4d). It
                costs nothing else - playlists, favourites and following are
                all public - so the only thing a sign-in would add here is
                Mixcloud telling us who you are, and this is that, typed once.
                If signing in ever works it fills this in and the rest of the
                page stays as it is.
              */
              <ServiceRow
                brand="mixcloud"
                info={mixcloudInfo}
                status={
                  ctx.settings.mixcloudUsername
                    ? `@${ctx.settings.mixcloudUsername}`
                    : 'No account needed'
                }
              >
                <form className={s.serviceForm} onSubmit={handleMixcloudUsernameSubmit}>
                  <input
                    className={s.settingInput}
                    id="mixcloud-username"
                    name="mixcloud-username"
                    type="text"
                    defaultValue={ctx.settings.mixcloudUsername || ''}
                    placeholder="your Mixcloud username"
                    aria-label="Your Mixcloud username"
                  />
                  <button className="button secondary">Save</button>
                  {ctx.settings.mixcloudUsername && (
                    <button
                      type="button"
                      className="button hollow"
                      onClick={() => ctx.setMixcloudUsername(undefined)}
                    >
                      Forget
                    </button>
                  )}
                </form>
              </ServiceRow>
            )}

            {spotifyEnabled && (
              <ServiceRow
                brand="spotify"
                info={spotifyInfo}
                // display_name, not email: Development Mode apps no longer
                // get the email field from /me (February 2026). It can be
                // empty on an account that never set one, hence the id.
                status={
                  spotifyCtx.accessToken &&
                  (spotifyCtx.userProfile?.display_name || spotifyCtx.userProfile?.id)
                }
              >
                {spotifyCtx.accessToken ? (
                  <button className="button" onClick={spotifyCtx.logout}>
                    Logout
                  </button>
                ) : (
                  <AuthorizeButton
                    busy={spotifyCtx.isAuthorizing}
                    onClick={() =>
                      spotifyCtx.authorize({ allowRedirect: true }).catch(ctx.callSnackbar)
                    }
                  >
                    Login
                  </AuthorizeButton>
                )}
              </ServiceRow>
            )}
          </ul>
        </section>

        <hr className={s.rule} />

        <div className={s.setting}>
          <div className={s.settingButtons}>
            <button className="button" onClick={libCtx.saveLibrary}>
              Save Library
            </button>
            <InfoBubble label={saveLibraryInfo.label}>{saveLibraryInfo.text}</InfoBubble>
          </div>
        </div>

        <div className={s.setting}>
          <div className={s.settingButtons}>
            <button
              className="button"
              disabled={!libCtx.libraryLoaded}
              onClick={() => setImportingItunes(true)}
            >
              Import iTunes playlists
            </button>
            <InfoBubble label={itunesImportInfo.label}>{itunesImportInfo.text}</InfoBubble>
          </div>
        </div>
        <div className={s.setting}>
          <div className={s.settingButtons}>
            <button className="button" onClick={showShortcuts}>
              Keyboard shortcuts
            </button>
          </div>
        </div>
        <ToggleSetting
          label="Confirm before deleting tracks from the library"
          checked={ctx.settings.confirmDelete}
          onChange={ctx.toggleConfirmDelete}
        />
        <ToggleSetting
          label="Playlist status bar"
          help="The counts along the bottom of a playlist."
          checked={ctx.settings.showPlaylistStatusBar}
          onChange={ctx.toggleShowPlaylistStatusBar}
        />

        <hr className={s.rule} />

        {/*
          Folded away by default. Everything in here is either destructive,
          only meaningful with one provider, or a preference nobody changes
          twice - and left open it was most of the page.

          <details> rather than a toggle in state: the browser remembers
          nothing across navigations either way, and this needs no JavaScript
          to work.
        */}
        <details className={s.advanced}>
          <summary className={s.advancedSummary}>Advanced</summary>

          {gapiCtx.gapiAuthorized && currentProvider === StorageProviderKey.GoogleDrive && (
            <div className={s.setting}>
              <div className={classNames('button-group', s.settingButtons)}>
                <button className="button" onClick={gapiCtx.resetAppDataFolder}>
                  Reset GoogleDrive AppData
                </button>
                <InfoBubble label={resetAppDataInfo.label}>{resetAppDataInfo.text}</InfoBubble>
                <button className="button" onClick={gapiCtx.downloadAppDataFolder}>
                  Download GoogleDrive AppData
                </button>
                <InfoBubble label={downloadAppDataInfo.label}>
                  {downloadAppDataInfo.text}
                </InfoBubble>
                {/*
                  Especially useful for changing YouTube channels since the
                  Google Api is extremely limited in this regard.
                  See Asana tickets on Binni's issue
                */}
                <button className="button" onClick={gapiCtx.disconnect}>
                  Revoke app permissions
                </button>
                <InfoBubble label={revokePermissionsInfo.label}>
                  {revokePermissionsInfo.text}
                </InfoBubble>
              </div>
            </div>
          )}

          <form className={s.setting} onSubmit={handleLibraryPathSubmit}>
            <label className={s.settingLabel} htmlFor="library-path">
              Podcast save path
              <InfoBubble label={podcastPathInfo.label}>{podcastPathInfo.text}</InfoBubble>
            </label>
            <div className={classNames(s.settingButtons, s.settingInputRow)}>
              <input
                className={s.settingInput}
                id="library-path"
                name="library-path"
                type="text"
                defaultValue={ctx.libraryPath?.toString()}
                placeholder="/onedrive/podcast/save/path"
              />
              <button className="button secondary">Submit</button>
            </div>
          </form>

          <ToggleSetting
            label="Google Drive id navigation"
            help="Navigate Google Drive by file or folder id rather than by browsing."
            checked={ctx.settings.enableGoogleDriveIdNavigation}
            onChange={ctx.toggleGoogleDriveIdNavigation}
          />

          {mixcloudEnabled && (
            <ToggleSetting
              label="Guess the DJ from Mixcloud show titles"
              help={
                <>
                  <p>
                    Mixcloud has no artist field at all, so a set played by one DJ but posted by an
                    archive or a radio account is credited to that account.
                  </p>
                  <p>
                    With this on, a title that plainly names the DJ - "Dax J @ Awakenings", or a
                    leading name the uploader also tagged - is used as the artist instead, and the
                    account stays in the Uploader column. Everything else keeps the uploader.
                  </p>
                  <p>
                    <b>This is the only place Fadila guesses at metadata</b>, and it only affects
                    shows added while it is on.
                  </p>
                </>
              }
              checked={ctx.settings.mixcloudArtistFromTitle}
              onChange={ctx.toggleMixcloudArtistFromTitle}
            />
          )}

          <ToggleSetting
            label="Lock the player at medium size"
            help="YouTube decides its own playback size, so the only way to stay on small is not to play YouTube tracks :)"
            checked={ctx.settings.lockPlayerSizeMedium}
            onChange={ctx.toggleLockPlayerSizeMedium}
          />

          {localStorageProviderEnabled && (
            <div className={s.setting}>
              <div className={s.settingButtons}>
                <button
                  className="button secondary"
                  disabled={currentProvider === StorageProviderKey.Local}
                  onClick={() => ctx.configureStorageProvider(StorageProviderKey.Local)}
                >
                  {currentProvider === StorageProviderKey.Local
                    ? 'Already storing in this browser'
                    : 'Store the library in this browser'}
                </button>
                <InfoBubble label={localStorageInfo.label}>{localStorageInfo.text}</InfoBubble>
              </div>
            </div>
          )}
        </details>
      </article>

      {importingItunes && <ImportItunes onClose={() => setImportingItunes(false)} />}
    </ErrorBoundary>
  );
}
